import React, { useRef, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useFormikContext } from 'formik'
import { ListGroup, Stack } from 'react-bootstrap'
import type { FunctionComponent } from 'react'
import type { PivotLayoutCalculatedMeasure } from '@ztechapps/common.contract'
import type { ModalTriggerProps } from '@ztechapps/webui.modal'
import type { PivotData } from '../pivot/types'
import type { PivotAggregationEnum } from '@ztechapps/common.contract'
import { compilePivotFormula, getWindow } from '@ztechapps/common.utils'
import { BusyButton } from '@ztechapps/webui.button'
import { Field, FormModal } from '@ztechapps/webui.form'
import { CheckboxInput, TextAreaInput, TextInput } from '@ztechapps/webui.input'
import { HorizontalStack } from '@ztechapps/webui.util'
import { PIVOT_AGGREGATION_LABELS } from '../pivot/constants'
import { filterPivotFields } from '../pivot/filterPivotFields'
import { toPivotDefaultAggregation } from '../pivot/pivotAggregation.utils'
import { usePivotViewerContext } from '../PivotViewer/Context'
import { PivotAggregationDropdown } from './PivotAggregationDropdown'

/** Props for the pivot calculated value editor. */
export type PivotCalculatedValueModalProps = {
  trigger: FunctionComponent<ModalTriggerProps>
  data: PivotData
  measure?: PivotLayoutCalculatedMeasure
  takenNames: string[]
  onApply: (measure: PivotLayoutCalculatedMeasure) => void
}

type CalculatedValueFormValues = { name: string; formula: string; individual: boolean }

type CalculatedValueFieldsProps = { data: PivotData }

const OPERATOR_BUTTONS: { label: string; token: string }[] = [
  { label: '+', token: ' + ' },
  { label: '-', token: ' - ' },
  { label: '×', token: ' * ' },
  { label: '÷', token: ' / ' },
  { label: '^', token: ' ^ ' },
  { label: '=', token: ' == ' },
  { label: '<', token: ' < ' },
  { label: '>', token: ' > ' },
  { label: '≤', token: ' <= ' },
  { label: '≥', token: ' >= ' },
  { label: '!=', token: ' != ' },
  { label: 'OR', token: ' or ' },
  { label: 'AND', token: ' and ' },
  { label: 'IF', token: 'if(' },
  { label: 'ABS', token: 'abs(' },
  { label: 'RD', token: 'round(' },
  { label: 'MIN', token: 'min(' },
  { label: 'MAX', token: 'max(' },
]

/** Name, field picker, individual flag, operator buttons and formula of the calculated value editor. */
const CalculatedValueFields = ({ data }: CalculatedValueFieldsProps) => {
  const { values, setFieldValue } = useFormikContext<CalculatedValueFormValues>()
  const [search, setSearch] = useState('')
  const [aggregations, setAggregations] = useState<Record<string, PivotAggregationEnum>>({})
  const formulaRef = useRef<HTMLTextAreaElement>(null)

  const insert = (token: string) => {
    const formula = values.formula
    const box = formulaRef.current
    // a button click moves focus off the box, so the caret reads only while the box holds focus
    const focused = box !== null && getWindow().document.activeElement === box
    const start = focused && box ? box.selectionStart : formula.length
    const end = focused && box ? box.selectionEnd : formula.length
    setFieldValue('formula', `${formula.slice(0, start)}${token}${formula.slice(end)}`)
    const caret = start + token.length
    getWindow().requestAnimationFrame(() => {
      const live = formulaRef.current
      if (!live) return
      live.focus()
      live.setSelectionRange(caret, caret)
    })
  }

  const visibleFields = filterPivotFields(data.fields, search)

  return (
    <Stack gap={2}>
      <Field name="name" label="Name" component={TextInput} />
      <TextInput placeholder="Search fields" value={search} onChange={(event) => setSearch(event.target.value)} />
      {/* the same cap as FieldZone's lists (FieldZone.tsx:100), so the dialog body does not scroll over the list's own scroll */}
      <ListGroup className="overflow-y-auto" style={{ maxHeight: '25vh' }}>
        {isEmpty(visibleFields) ? (
          <ListGroup.Item className="py-1 px-2 small text-secondary">{`No fields match "${search}"`}</ListGroup.Item>
        ) : (
          visibleFields.map((field) => {
            const aggregation = aggregations[field.name] ?? toPivotDefaultAggregation(field)
            return (
              <ListGroup.Item key={field.name} className="position-static py-1 px-2 small">
                <HorizontalStack gap={2}>
                  {/* the label inserts the field on click but renders as plain text, like the Fields panel rows */}
                  <div className="flex-grow-1" onClick={() => insert(`${aggregation.toLowerCase()}("${field.name}")`)}>
                    {`${field.name} (${PIVOT_AGGREGATION_LABELS[aggregation]})`}
                  </div>
                  <PivotAggregationDropdown
                    field={field}
                    label={field.name}
                    aggregation={aggregation}
                    onSelect={(selected) => setAggregations((prev) => ({ ...prev, [field.name]: selected }))}
                  />
                </HorizontalStack>
              </ListGroup.Item>
            )
          })
        )}
      </ListGroup>
      <Field name="individual" label="Calculate individual values" component={CheckboxInput} onChange />
      <div role="group" aria-label="Formula operators" className="btn-group btn-group-sm flex-wrap">
        {OPERATOR_BUTTONS.map((button) => (
          <BusyButton
            key={button.label}
            variant="outline-secondary"
            text={button.label}
            onClick={() => insert(button.token)}
          />
        ))}
      </div>
      <Field
        name="formula"
        label="Formula"
        component={TextAreaInput}
        rows={4}
        inputRef={formulaRef}
        className="font-monospace"
      />
    </Stack>
  )
}

/** Calculated value editor — adds a measure to Values or edits one in place. */
export const PivotCalculatedValueModal = ({
  trigger,
  data,
  measure,
  takenNames,
  onApply,
}: PivotCalculatedValueModalProps) => {
  const { container } = usePivotViewerContext()

  return (
    <FormModal<CalculatedValueFormValues>
      trigger={trigger}
      title="Calculated value"
      saveText="Apply"
      size="lg"
      resizable
      resizeStorageKey="pivot-calculated-value"
      // the formula box holds focus on open, so the first press on a Σ, operator or field row blurs it; a blur-time
      // validate would insert an error line above the rows, and the press's mouseup would miss — the same
      // blur-then-shift click loss as libs/webui/auth/src/Register/Layout.tsx:12-25. Change and submit still validate.
      validateOnBlur={false}
      container={container}
      initialValues={
        measure
          ? { name: measure.field, formula: measure.formula, individual: measure.individual ?? false }
          : { name: '', formula: '', individual: false }
      }
      validate={(values) => {
        const errors: { name?: string; formula?: string } = {}
        const name = values.name.trim()
        if (isEmpty(name)) errors.name = 'Enter a name'
        else if (takenNames.includes(name)) errors.name = `${name} is already taken`
        try {
          const compiled = compilePivotFormula(values.formula)
          const fieldNames = new Set(data.fields.map((field) => field.name))
          const unknown = compiled.references.find((reference) => !fieldNames.has(reference.field))
          if (unknown) errors.formula = `Unknown field ${unknown.field}`
        } catch (error) {
          errors.formula = error instanceof Error ? error.message : String(error)
        }
        return errors
      }}
      onSubmit={({ name, formula, individual }) =>
        onApply({
          ...(measure?.format ? { format: measure.format } : {}),
          field: name.trim(),
          formula,
          ...(individual ? { individual: true } : {}),
        })
      }
    >
      <CalculatedValueFields data={data} />
    </FormModal>
  )
}
